/**
 * Is the world still reaching us? — one monitor, two failure modes, one banner.
 *
 * A browser flight simulator streaming from two public buckets has two ways to lose its ground,
 * and they need different words:
 *
 *   - **offline** — the machine has no network. `navigator.onLine` says so, and the pilot already
 *     knows; what they need to be told is that the aeroplane is *fine*, because the terrain they
 *     have already flown over is in the Cache API and the simulation never touched the network in
 *     the first place.
 *   - **degraded** — the machine is online and the tile source is not answering. This one the
 *     pilot cannot diagnose at all: the ground simply stops sharpening ahead of them and they
 *     assume the simulator is broken. It is the free bucket having a bad afternoon.
 *
 * ## Why `navigator.onLine` is not enough on its own
 *
 * It answers "is there a network interface with a route", which is not the question. It reads
 * `true` behind a captive portal, on a hotel wifi that has not been paid for, and on a VPN that
 * dropped its tunnel — every case where the tiles stop arriving and the browser is content. So the
 * second signal is the terrain's own cumulative failure count, which is ground truth: those are
 * requests that were tried four times, backed off between attempts, and still did not arrive.
 *
 * Failures are counted as a **rate**, not a total. A long flight legitimately accumulates a few
 * failures at the edges of coverage over an hour, and a banner triggered by a lifetime total would
 * appear once and never leave. What matters is whether they are happening *now*.
 *
 * `updateFixed` / `updateFrame`: `updateFrame` — polled from the loop's after-frame slot. It
 * allocates nothing and does integer arithmetic on three fields.
 */

/** What to tell the pilot, or nothing at all. */
export type ConnectionStatus = 'online' | 'offline' | 'degraded';

export interface ConnectionMonitorOptions {
  /** New tile failures inside the window before the source is called degraded. */
  failureThreshold?: number;
  /** How long a failure counts towards the threshold [ms]. */
  windowMs?: number;
  /**
   * Quiet time before a degraded source is declared recovered [ms].
   *
   * Longer than the window, deliberately. A source that is flapping — a minute up, a minute down —
   * should read as one continuous problem rather than as a banner blinking on and off, which is
   * far more irritating than the problem it is reporting.
   */
  recoveryMs?: number;
}

export class ConnectionMonitor {
  readonly failureThreshold: number;
  readonly windowMs: number;
  readonly recoveryMs: number;

  private statusValue: ConnectionStatus = 'online';
  /** Cumulative failures at the last sample, so the delta is what happened since. */
  private lastFailures = -1;
  /** Failures inside the current window. */
  private windowFailures = 0;
  private windowOpenedMs = 0;
  /** When the last failure was seen, so recovery can be timed from it. */
  private lastFailureMs = Number.NEGATIVE_INFINITY;

  constructor(options: ConnectionMonitorOptions = {}) {
    this.failureThreshold = Math.max(1, options.failureThreshold ?? 4);
    this.windowMs = options.windowMs ?? 10_000;
    this.recoveryMs = options.recoveryMs ?? 15_000;
  }

  get status(): ConnectionStatus {
    return this.statusValue;
  }

  /**
   * Sample the two signals and return the status.
   *
   * @param tileFailures `TerrainStats.failed` — cumulative since start-up, so it only ever grows.
   * @param navigatorOnline `navigator.onLine`, injected rather than read, so the tests can be
   *   about the logic instead of about a global.
   */
  updateFrame(nowMs: number, tileFailures: number, navigatorOnline: boolean): ConnectionStatus {
    /*
     * First sample seeds the baseline rather than counting.
     *
     * Without this, a session that starts with a warm failure count — a reload after an outage,
     * or a `?icao=` deep into the ocean — would see the entire history as having happened in the
     * first frame and open with a banner about a source that is answering perfectly well.
     */
    if (this.lastFailures < 0) {
      this.lastFailures = tileFailures;
      this.windowOpenedMs = nowMs;
      this.statusValue = navigatorOnline ? 'online' : 'offline';
      return this.statusValue;
    }

    const fresh = Math.max(0, tileFailures - this.lastFailures);
    this.lastFailures = tileFailures;

    if (nowMs - this.windowOpenedMs > this.windowMs) {
      this.windowFailures = 0;
      this.windowOpenedMs = nowMs;
    }
    if (fresh > 0) {
      this.windowFailures += fresh;
      this.lastFailureMs = nowMs;
    }

    /*
     * Offline wins over degraded whenever the browser is sure.
     *
     * They are not exclusive — an offline machine's tiles fail too, so both signals fire — and of
     * the two, "you have no network" is the more useful sentence: it is the one the pilot can act
     * on, and it explains the other.
     */
    if (!navigatorOnline) {
      this.statusValue = 'offline';
      return this.statusValue;
    }

    if (this.windowFailures >= this.failureThreshold) {
      this.statusValue = 'degraded';
      return this.statusValue;
    }

    // Coming back: from offline the moment the browser says so, from degraded only after the
    // failures have genuinely stopped for a while.
    if (this.statusValue === 'degraded' && nowMs - this.lastFailureMs < this.recoveryMs) {
      return this.statusValue;
    }

    this.statusValue = 'online';
    return this.statusValue;
  }

  /**
   * Forget the failure history, keeping the connection state.
   *
   * For a teleport: spawning at a new airport legitimately misses tiles at the edge of what the
   * new region publishes, and those misses say nothing about the source's health.
   */
  resetFailureWindow(nowMs: number): void {
    this.windowFailures = 0;
    this.windowOpenedMs = nowMs;
    this.lastFailureMs = Number.NEGATIVE_INFINITY;
    this.lastFailures = -1;
  }
}

/** The sentence that goes on the banner. Separated so the wording is reviewable in one place. */
export function connectionMessage(status: ConnectionStatus): string | null {
  switch (status) {
    case 'offline':
      // The reassurance is the point: the flight model and the audio never touch the network, and
      // terrain already visited is on disk. Saying "offline" alone reads as "it is about to stop".
      return 'Offline — still flying, on terrain already cached. New ground will fill in when the network is back.';
    case 'degraded':
      return 'The terrain source is not responding — flying on cached tiles. Nothing else is affected.';
    case 'online':
      return null;
  }
}
